import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Signal, ViewChild, computed, effect, isDevMode, signal } from '@angular/core';
import { StoredFilter } from './models/filter-models';
import { Comparator, Rarity } from "./models/enums";
import { FormsModule } from '@angular/forms';
import { QuickFilterTabComponent } from './quick-filter-tab/quick-filter-tab.component';
import { CosmeticTabComponent } from './cosmetic-tab/cosmetic-tab.component';
import { CustomRulesTabComponent } from './custom-rules-tab/custom-rules-tab.component';
import { FreeRulesTabComponent } from './free-rules-tab/free-rules-tab.component';
import { TierListTabComponent } from './tier-list-tab/tier-list-tab.component';
import { LOCAL_STORAGE_KEY_HIDE_COPIES_WARNING, VERSION, LOCAL_STORAGE_KEY_LAST_VERSION, LOCAL_STORAGE_KEY_REVISIT, LOCAL_STORAGE_KEY_RESET_STORAGE, LEAGUE_NAME, DISABLE_SYNC_UNTIL_REALM_DOWN } from './const';
import { VERSION_WITHOUT_BUILD } from '../version';
import { FilterResultData } from './services/filter-generator.service';
import { SettingsTabComponent } from './settings-tab/settings-tab.component';
import { Router, NavigationEnd } from '@angular/router';
import { ChangelogTabComponent } from './changelog-tab/changelog-tab.component';
import { ImportFilterDialogComponent } from './import-filter-dialog/import-filter-dialog.component';
import { FilterImportResults, FilterSectionsToUpdate } from './models/filter-import.models';
import { ProgressPresetsComponent } from './progress-presets/progress-presets.component';
import { LoginComponent } from './login/login.component';
import { FilterService } from './services/filter.service';
import { ActiveFilterComponent } from './active-filter/active-filter.component';
import { FilterSelectorDialogComponent } from './filter-selector-dialog/filter-selector-dialog.component';
import { PreviewTabComponent } from './preview-tab/preview-tab.component';
import { TooltipWrapperComponent } from './common/tooltip-wrapper/tooltip-wrapper.component';
import { LegendComponent } from './legend/legend.component';
import { ClassPresetsComponent } from './class-presets/class-presets.component';
import { ResponsiveService } from './services/responsive.service';
import { BaseComponent } from './common/base.component';
import { PresetsTabComponent } from './presets-tab/presets-tab.component';
import { ContentLoaderService } from './services/content-loader.service';
import { ExtraTabComponent } from './info-tab/extra-tab.component';
import { ControlsComponent } from './controls/controls.component';
import { CtrlKeyService } from './services/ctrl-key.service';
import { NavigationService } from './services/navigation.service';
import { GameItem } from './models/game-item.models';
import { SimulatorResult, SimulatorService } from './services/simulator.service';
import { GameItemViewerComponent } from './game-item-viewer/game-item-viewer.component';
import { GameItemParserService } from './services/game-item-parser.service';
import { TabService, TabType, tabFromPath } from './services/tab.service';
import { ToastService } from './services/toast.service';
import { EconomyService } from './services/economy.service';
import { Economy, EcoUpdateBehavior } from './models/economy-models';
import { OAuthService } from './services/o-auth.service';
import { PoeApiService, FilterUpdateDelayedError, PoeApiReportedError } from './services/poe-api.service';
import { FileSaveService } from './services/file-save.service';
import { ACCOUNT_FILTER_DEFAULT_REALM, abbrevGameName } from '../game';
import { LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM } from './const';
import { logEvent, trackError } from './app.config';
import { ItemLibraryService } from './services/item-library.service';
import { computeItemHash } from './utils/item-hash';
import { PresetPreviewService } from './services/preset-preview.service';
import { PresetChangesDialogComponent } from './preset-changes-dialog/preset-changes-dialog.component';
import { UserOnboardingService } from './services/user-onboarding.service';
// import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [FormsModule, QuickFilterTabComponent, CosmeticTabComponent, CustomRulesTabComponent,
    FreeRulesTabComponent, TierListTabComponent, SettingsTabComponent, ChangelogTabComponent,
    ImportFilterDialogComponent, ProgressPresetsComponent, ClassPresetsComponent, LoginComponent, ActiveFilterComponent, PreviewTabComponent, FilterSelectorDialogComponent,
    TooltipWrapperComponent, LegendComponent, PresetsTabComponent, ControlsComponent, GameItemViewerComponent,
    ExtraTabComponent, PresetChangesDialogComponent
    // DatePipe
    ]
})
export class AppComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  storedFilter!: Signal<StoredFilter>;
  filterResults!: Signal<FilterResultData>;

  importFilterResults = new FilterImportResults();

  initialVisit = true;
  filterResetWarning = false;
  showCopiesWarning = false;
  showPresetsDialog = false;
  showClassPresets = false;
  showResetWarning = false;
  showIncomingPatchWarning = false;
  showIncomingPatchTopBar = false;

  filterVersion = VERSION;
  versionDisplayText = `v${VERSION}`;

  showImportDialog = false;
  showFilterSelectorDialog = false;

  showItemDetails = false;
  pastedItemText = '';
  gameItem?: GameItem;
  parseItemError?: string;
  simulatorResult?: SimulatorResult;

  isBeta = false;
  isLegacy = false;

  Rarity = Rarity;
  Comparator = Comparator;
  EcoUpdateBehavior = EcoUpdateBehavior;

  hasPendingUpdates!: Signal<boolean>;
  showUpdatedMessage = false;
  showPendingUpdateMessage = false;
  lastUpdateTimeFormatted = '';
  updateTabBlinking = false;

  private updateTabBlinkTimeout: ReturnType<typeof setTimeout> | null = null;

  leagueName = LEAGUE_NAME;

  @ViewChild('filterOutput') filterOutputRef?: ElementRef<HTMLElement>;

  private stickyObserver: IntersectionObserver | null = null;
  private stickySentinel: HTMLElement | null = null;
  private stickyNavigationEl: HTMLElement | null = null;
  private filterOutputObserver: IntersectionObserver | null = null;
  private floatingControlsObserver: IntersectionObserver | null = null;

  private static readonly FLOATING_CONTROLS_TABS: TabType[] =
    ['quick-filters', 'tier-list', 'custom-rules', 'cosmetic'];

  readonly inlineControlsInView = signal(false);

  readonly floatingControlsEligible = computed(() =>
    this.settings.floatingControls() &&
    AppComponent.FLOATING_CONTROLS_TABS.includes(this.tabService.currentTab())
  );

  readonly showInlineControls = computed(() => {
    const tab = this.tabService.currentTab();
    return tab !== 'updates' && tab !== 'settings';
  });

  readonly showFloatingControls = computed(() =>
    this.floatingControlsEligible() && !this.inlineControlsInView()
  );

  constructor(private router: Router,
    public filterService: FilterService, private responsiveService: ResponsiveService,
    private contentLoader: ContentLoaderService,
    public ctrlKeyService: CtrlKeyService,
    private navigationService: NavigationService,
    private gameItemParserService: GameItemParserService,
    private simulatorService: SimulatorService,
    public tabService: TabService,
    private economyService: EconomyService,
    private loginService: OAuthService,
    private poeApiService: PoeApiService,
    private fileSaveService: FileSaveService,
    public toastService: ToastService,
    private itemLibraryService: ItemLibraryService,
    public presetPreviewService: PresetPreviewService,
    private userOnboarding: UserOnboardingService,
  ) {
    super();
    this.storedFilter = this.filterService.activeFilter;
    this.filterResults = this.filterService.filterResultData;
    this.hasPendingUpdates = this.economyService.hasPendingUpdates;

    // Re-parse item when filterResultData changes and pastedItemText is not blank
    effect(() => {
      this.filterResults(); // Track the signal
      if (this.pastedItemText) {
        this.parseItem();
      }
    });

    // Watch for pending updates and handle auto-update based on setting
    effect(() => {
      if (this.hasPendingUpdates()) {
        if (this.filterService.activeFilter().filter.economy === Economy.Fixed) {
          // Just silently apply pending updates
          this.economyService.applyPendingUpdates();
          this.economyService.setLastUpdateType('update', false, false);
          return;
        }

        const behavior = this.settings.ecoUpdateBehaviorUpdate();

        if (behavior === EcoUpdateBehavior.Inform) {
          this.showPendingUpdateMessage = true;
        } else {
          // Auto-apply updates
          this.updateEconomy(false);

          // If AutoUpdateAndSync also sync to account if user is logged in, and save to file if we have a file handle.
          if (behavior === EcoUpdateBehavior.AutoUpdateAndSync && !DISABLE_SYNC_UNTIL_REALM_DOWN) {
            this.saveFilterToFileIfLinked();
            if (this.loginService.userName()) {
              this.economyService.lastUpdateSyncing = true;
              this.performSync().then((success) => {
                this.economyService.setLastUpdateType('update', success !== undefined, success === false);
              }).finally(() => {
                this.economyService.lastUpdateSyncing = false;
              });
            }
          }

          if (this.tabService.currentTab() !== 'updates') {
            this.showEconomyUpdatedTab();
          }
        }
      }
    });

    effect(() => {
      if (this.economyService.updatedOnLaunch()) {
        this.economyService.updatedOnLaunch.set(false);

        if (this.initialVisit) {
          // We can't yet be logged in on initial visit, and we don't care about an 'update', so just ignore
          return;
        }

        if (this.filterService.activeFilter().filter.economy === Economy.Fixed) {
          // Changes were automatically applied on launch, we don't need to do anything else
          return;
        }

        const behavior = this.settings.ecoUpdateBehaviorLaunch();
        const showUpdates = this.settings.ecoUpdateBehaviorLaunchShowUpdates();

        // If AutoUpdateAndSync and user is logged in, also sync
        if (behavior === EcoUpdateBehavior.AutoUpdateAndSync && !DISABLE_SYNC_UNTIL_REALM_DOWN) {
          this.saveFilterToFileIfLinked();

          if (this.loginService.userName()) {
            this.economyService.lastUpdateSyncing = true;
            this.performSync().then((success) => {
              this.economyService.setLastUpdateType('update', success !== undefined, success === false);
            }).finally(() => {
              this.economyService.lastUpdateSyncing = false;
            });
          }
        }

        if (showUpdates) {
          this.goToChangelog();
        } else if (this.tabService.currentTab() !== 'updates') {
          this.showEconomyUpdatedTab();
        }
      }
    });

    effect((onCleanup) => {
      const enabled = this.settings.stickyNavigation();
      const timer = setTimeout(() => {
        if (enabled) {
          this.enableStickyNavigation();
        } else {
          this.disableStickyNavigation();
        }
      }, 100);
      onCleanup(() => {
        clearTimeout(timer);
        this.disableStickyNavigation();
      });
    });

    effect((onCleanup) => {
      const eligible = this.floatingControlsEligible();
      const timer = setTimeout(() => {
        if (eligible) {
          this.enableFloatingControlsObserver();
        } else {
          this.disableFloatingControlsObserver();
          this.inlineControlsInView.set(false);
        }
      }, 100);
      onCleanup(() => {
        clearTimeout(timer);
        this.disableFloatingControlsObserver();
      });
    });
  }

  ngOnInit(): void {
    if (isDevMode()) {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      window.scrollTo(0, 0);
    }

    this.initializeEnvironmentFlags();

    // Initialize content preloading
    this.contentLoader.initializePreloading().subscribe((success: boolean) => {
      if (!success) {
        console.warn('Content preloading failed, will load on-demand');
      }
    });

    const lastVersion = localStorage.getItem(LOCAL_STORAGE_KEY_LAST_VERSION);

    if (localStorage.getItem(LOCAL_STORAGE_KEY_RESET_STORAGE) === '1') {
      this.showResetWarning = true;
      localStorage.removeItem(LOCAL_STORAGE_KEY_RESET_STORAGE);
    }

    // this.showCopiesWarning = localStorage.getItem(LOCAL_STORAGE_KEY_HIDE_COPIES_WARNING) !== '1';
    this.showIncomingPatchWarning = this.poe2 && this.settings.showIncomingPatchWarning();
    this.showIncomingPatchTopBar = this.poe2 && this.settings.showIncomingPatchTopBar();
    const hadVisitedBefore = !!localStorage.getItem(LOCAL_STORAGE_KEY_REVISIT);
    this.initialVisit = !hadVisitedBefore;
    this.userOnboarding.initializeOnAppLoad(hadVisitedBefore);

    const fallbackTab = this.settings.quickFiltersAsMainTab() ? 'quick-filters' : 'presets';

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const urlTab = tabFromPath(event.urlAfterRedirects);
        this.tabService.setTab(urlTab ?? fallbackTab);
      }
    });

    let defaultRoute: TabType | null = null;
    const autoViewChangelog = this.settings.autoViewChangelog();
    const ecoUpdateBehaviorLaunchShowUpdates = this.settings.ecoUpdateBehaviorLaunchShowUpdates();

    // Compare version without build number (only major.minor.patch for migration changes)
    const lastVersionWithoutBuild = lastVersion ? (() => {
      const parts = lastVersion.split('.');
      // If 4 parts (new format), use first 3. If 3 parts (old format), use as-is.
      return parts.length === 4 ? parts.slice(0, 3).join('.') : lastVersion;
    })() : null;
    const hasNewVersion = lastVersionWithoutBuild !== VERSION_WITHOUT_BUILD;
    const hasMovedCurrencyOrUniques = this.economyService.lastUpdateType === 'launch';
    if (this.initialVisit) {
      defaultRoute = 'presets';
    } else if (autoViewChangelog && hasNewVersion) {
      defaultRoute = 'updates';
    } else if (ecoUpdateBehaviorLaunchShowUpdates && hasMovedCurrencyOrUniques) {
      defaultRoute = 'updates';
    } else if (!this.settings.quickFiltersAsMainTab()) {
      defaultRoute = 'presets';
    }

    // Explicit URL path wins over default tab; defaults apply only for bare /
    const urlTab = tabFromPath(window.location.pathname);
    this.tabService.setTab(urlTab ?? defaultRoute ?? fallbackTab);

    localStorage.setItem(LOCAL_STORAGE_KEY_LAST_VERSION, this.filterVersion);
    localStorage.setItem(LOCAL_STORAGE_KEY_REVISIT, '1');

    this.responsiveService.isMobile.set(window.innerWidth <= 650);
  }

  private initializeEnvironmentFlags() {
    const hostname = window.location.hostname;

    if (hostname.startsWith('beta.')) {
      this.isBeta = true;
    } else if (hostname.startsWith('legacy.')) {
      this.isLegacy = true;
    }
  }

  ngAfterViewInit(): void {
    if (isDevMode()) {
      window.scrollTo(0, 0);
    }

    const el = this.filterOutputRef?.nativeElement;
    if (!el) return;

    this.filterOutputObserver = new IntersectionObserver(
      ([entry]) => this.filterOutputService.setInViewport(entry.isIntersecting),
      { threshold: 0 },
    );
    this.filterOutputObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.filterOutputObserver?.disconnect();
    this.filterOutputObserver = null;
    this.disableFloatingControlsObserver();
    this.clearUpdateTabBlinkTimer();
  }

  selectTab(tab: TabType) {
    if (tab === 'updates') {
      this.clearEconomyUpdatedTabState();
      this.showPendingUpdateMessage = false;
    }

    this.tabService.selectTab(tab);
  }

  updateEconomy(show = false) {
    this.economyService.applyPendingUpdates();
    this.filterService.updateAllEconomyTiers();
    this.filterService.updateFilter(true);
    this.economyService.setLastUpdateType('update', false, false);

    if (show && this.tabService.currentTab() !== 'updates') {
      this.showPendingUpdateMessage = false;
      this.tabService.selectTab('updates');
    }
  }

  private saveFilterToFileIfLinked(): void {
    if (!this.fileSaveService.supportsFileSystemAccess) return;
    const stored = this.storedFilter();
    if (!this.fileSaveService.knownHandleNames().has(stored.name)) return;
    const extension = stored.filter.ruthless ? '.ruthlessfilter' : '.filter';
    const filename = stored.name + extension;
    void this.fileSaveService.save(this.filterResults().filterTextFull, stored.name, filename);
  }

  private async performSync(): Promise<boolean | undefined> {
    if (!this.loginService.userName()) {
      return undefined;
    }

    try {
      const realm = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM) || ACCOUNT_FILTER_DEFAULT_REALM;
      const storedFilter = this.storedFilter();
      const filterResults = this.filterResults();
      const filterType = storedFilter.filter.ruthless ? 'Ruthless' : undefined;

      if (!storedFilter.id) {
        const poeFilter = await this.poeApiService.createFilter(storedFilter.name, filterResults.filterTextFull, realm, filterType);
        storedFilter.id = poeFilter.id;
        this.filterService.updateFilter();
      } else {
        await this.poeApiService.updateFilter(storedFilter.id, storedFilter.name, filterResults.filterTextFull, realm, filterType);
      }
      this.toastService.raise(
        'success',
        'Your filter was synced to pathofexile.com. Refresh it in-game if needed.',
        `Synced to ${abbrevGameName}`,
      );
      return true;
    } catch (error) {
      console.error('Auto-sync failed:', error);
      if (error instanceof FilterUpdateDelayedError || error instanceof PoeApiReportedError) {
        return false;
      }
      const message = error instanceof Error ? error.message : 'Auto-sync failed.';
      this.toastService.raise('error', message, 'Auto-sync failed');
      return false;
    }
  }

  goToChangelog() {
    this.clearEconomyUpdatedTabState();
    this.tabService.selectTab('updates');
  }

  private showEconomyUpdatedTab(): void {
    this.showUpdatedMessage = true;
    this.lastUpdateTimeFormatted = this.formatUpdateTime(new Date());
    this.clearUpdateTabBlinkTimer();
    if (this.settings.ecoUpdateBehaviorUpdateBlink()) {
      this.updateTabBlinking = true;
      this.updateTabBlinkTimeout = setTimeout(() => {
        this.updateTabBlinking = false;
        this.updateTabBlinkTimeout = null;
      }, 60_000);
    } else {
      this.updateTabBlinking = false;
    }
  }

  private clearEconomyUpdatedTabState(): void {
    this.showUpdatedMessage = false;
    this.lastUpdateTimeFormatted = '';
    this.updateTabBlinking = false;
    this.clearUpdateTabBlinkTimer();
  }

  private clearUpdateTabBlinkTimer(): void {
    if (this.updateTabBlinkTimeout !== null) {
      clearTimeout(this.updateTabBlinkTimeout);
      this.updateTabBlinkTimeout = null;
    }
  }

  private formatUpdateTime(date: Date): string {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  handleQuickImport(importResult: FilterImportResults) {
    this.importFilterResults = importResult;
  }

  handleCustomImport(importResult: FilterImportResults) {
    this.importFilterResults = importResult;
    if (importResult.importFilterSuccess) {
      // This came from custom import, show the dialog
      this.importFilterResults.importFilterSuccess = false; // We don't yet want to show the success message
      this.showImportDialog = true;
    }
    // Errors are automatically handled by the template which checks importFilterResults.importFilterError
  }

  closeImportDialog() {
    this.showImportDialog = false;
  }

  onPresetChangeConfirm() {
    this.presetPreviewService.confirmChange();
    this.closePresetsDialog();
  }

  onPresetChangeCancel() {
    this.presetPreviewService.cancelChange();
    this.closePresetsDialog();
  }

  closePresetsDialog() {
    this.showPresetsDialog = false;
    this.showClassPresets = false;
  }

  updateFilterSections(sections: FilterSectionsToUpdate) {
    this.filterService.updateFilterSections(this.importFilterResults.filter!, sections);
    this.showImportDialog = false;
    this.importFilterResults.importFilterSuccess = true; // Only now we want to show the success message
  }

  hideCopiesWarning() {
    this.showCopiesWarning = false;
    localStorage.setItem(LOCAL_STORAGE_KEY_HIDE_COPIES_WARNING, '1');
  }

  hideIncomingPatchWarning() {
    this.showIncomingPatchWarning = false;
    this.settings.dismissIncomingPatchWarning();
  }

  hideIncomingPatchTopBar() {
    this.showIncomingPatchTopBar = false;
    this.settings.dismissIncomingPatchTopBar();
  }

  @HostListener('window:resize')
  onWindowResize() {
    let isMobile = window.innerWidth <= 650 ||
      (this.tabService.currentTab() === 'quick-filters' && window.innerWidth >= 1200 && window.innerWidth <= 1400);
    this.responsiveService.isMobile.set(isMobile);
  }

  @HostListener('window:keydown', ['$event'])
  async onKeyDown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) {
      this.ctrlKeyService.isCtrlHeld.set(true);
      document.body.classList.add('ctrl-held');

      // Check if Ctrl+V (or Cmd+V on Mac) is pressed
      if (event.key === 'v') {
        // Check if user is in a text input field
        const activeElement = document.activeElement;
        const isTextInput = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.hasAttribute('contenteditable')
        );

        // Only process if NOT in a text input field
        if (!isTextInput) {
          try {
            // Read clipboard contents
            const clipboardText = await navigator.clipboard.readText();

            // Check if clipboard text starts with "Item Class: "
            if (clipboardText.startsWith('Item Class: ')) {
              // Set showItemDetails to true
              this.showItemDetails = true;

              // Store clipboard text
              this.pastedItemText = clipboardText;

              this.parseItem(true);

              setTimeout(() => {
              // Scroll to the game item viewer
              const gameItemViewer = document.getElementById('game-item-viewer');
                if (gameItemViewer) {
                  gameItemViewer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 50);
            }
          } catch (clipboardError) {
            // Silently fail if clipboard access is denied or unavailable
            // This is expected in some browsers or when clipboard API is not available
            console.warn('Could not access clipboard:', clipboardError);
          }
        }
      }
    }
  }

  private parseItem(isNewPaste = false) {
    // Clear previous error and result
    this.parseItemError = undefined;
    this.gameItem = undefined;
    this.simulatorResult = undefined;

    try {
      // Parse the item
      const parsedItem = this.gameItemParserService.parseItem(this.pastedItemText);

      this.gameItem = parsedItem;
      this.simulatorResult = this.simulatorService.simulate(parsedItem);

      if (isNewPaste) {
        void this.itemLibraryService.add(parsedItem, this.pastedItemText);
        void this.reportParsedItem(parsedItem);
        this.playSimulatorAlertSound(this.simulatorResult);
      }
    } catch (parseError) {
      // Store parse error
      this.parseItemError = parseError instanceof Error ? parseError.message : 'Failed to parse item';
    }
  }

  private playSimulatorAlertSound(result: SimulatorResult): void {
    if (!result.shown) return;

    const cosmeticOptions = result.displayOptions?.cosmeticOptions;
    if (!cosmeticOptions?.alertSound || cosmeticOptions.customAlertSound) return;

    const sound = cosmeticOptions.alertSoundSound;
    if (!sound) return;

    const audio = new Audio();
    audio.src = `/sounds/sound${sound}.mp3`;
    audio.volume = Math.min(1, (cosmeticOptions.alertSoundVolume ?? 200) / 300);
    audio.load();
    void audio.play();
  }

  private async reportParsedItem(item: GameItem) {
    const itemJson = JSON.stringify(item);
    const itemHash = await computeItemHash(this.pastedItemText, itemJson);
    void fetch('/api/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        item_json: itemJson,
        item_text: this.pastedItemText,
        item_hash: itemHash,
        user_name: this.loginService.userName() ?? null,
      }),
    }).catch(ex => trackError(ex));
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (!event.ctrlKey && !event.metaKey) {
      this.ctrlKeyService.isCtrlHeld.set(false);
      document.body.classList.remove('ctrl-held');
    }
  }

  onFilterOutputClick(event: MouseEvent) {
    if (event.ctrlKey || event.metaKey) {
      // Find the closest element with data-rule-id attribute
      let target = event.target as HTMLElement;
      let ruleBlock: HTMLElement | null = null;

      // Traverse up the DOM tree to find the rule-block element
      while (target && target !== event.currentTarget) {
        if (target.hasAttribute('data-rule-id')) {
          ruleBlock = target;
          break;
        }
        target = target.parentElement as HTMLElement;
      }

      if (ruleBlock) {
        const ruleId = ruleBlock.getAttribute('data-rule-id');
        if (ruleId) {
          event.preventDefault();
          event.stopPropagation();
          this.navigationService.navigateToFilterTitle(ruleId);
        }
      }
    }
  }

  openFilterSelector($event: Event) {
    $event.preventDefault();
    this.showFilterSelectorDialog = true;
  }

  copyVersionToClipboard() {
    navigator.clipboard.writeText('v' + this.filterVersion);
    this.versionDisplayText = 'Version copied';
    setTimeout(() => {
      this.versionDisplayText = `v${this.filterVersion}`;
    }, 1000);
  }

  private enableStickyNavigation(): void {
    this.disableStickyNavigation();

    const navigation = document.querySelector('#trade .navigation') as HTMLElement;
    if (!navigation) return;

    this.stickyNavigationEl = navigation;
    navigation.classList.add('sticky-enabled');

    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          navigation.classList.add('sticky-active');
        } else {
          navigation.classList.remove('sticky-active');
        }
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      }
    );

    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.position = 'absolute';
    sentinel.style.top = '0';
    sentinel.style.left = '0';
    sentinel.style.width = '100%';
    sentinel.style.pointerEvents = 'none';

    navigation.parentNode?.insertBefore(sentinel, navigation);
    this.stickySentinel = sentinel;
    this.stickyObserver.observe(sentinel);
  }

  private disableStickyNavigation(): void {
    this.stickyObserver?.disconnect();
    this.stickyObserver = null;

    if (this.stickySentinel) {
      this.stickySentinel.remove();
      this.stickySentinel = null;
    }

    if (this.stickyNavigationEl) {
      this.stickyNavigationEl.classList.remove('sticky-enabled', 'sticky-active');
      this.stickyNavigationEl = null;
    }
  }

  private enableFloatingControlsObserver(): void {
    this.disableFloatingControlsObserver();

    const anchor = document.querySelector('#trade .inline-controls-anchor') as HTMLElement;
    if (!anchor) return;

    this.floatingControlsObserver = new IntersectionObserver(
      ([entry]) => {
        this.inlineControlsInView.set(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    this.floatingControlsObserver.observe(anchor);
  }

  private disableFloatingControlsObserver(): void {
    this.floatingControlsObserver?.disconnect();
    this.floatingControlsObserver = null;
  }
}
